import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Tabs, Tab, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { createProject, fetchProcessParameters, fetchQualityAttributes, fetchBioreactorData, updateProcessParameterType } from '../services/api';
import { getCachedApiUrl } from '../config/apiConfig';
import ProcessParametersSection from './ProcessParametersSection';
import './ProjectModal.css';

function ProjectModal({ show, onHide, onProjectCreated, readOnlyMode = false, existingProject = null }) {
  const [activeTab, setActiveTab] = useState('details');
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [isLoadingReferenceData, setIsLoadingReferenceData] = useState(true);
  const [referenceDataError, setReferenceDataError] = useState('');
  const isMountedRef = useRef(true);const [referenceData, setReferenceData] = useState({
    projectScales: [],
    projectTypes: [],
    successParameters: []
  });  const [processParameters, setProcessParameters] = useState([]);
  const [qualityAttributes, setQualityAttributes] = useState([]);
  const [bioreactorData, setBioreactorData] = useState([]);
  const [formData, setFormData] = useState({
    // Project Details
    projectName: '',
    projectOwner: '',
    scale: '',
    type: '',
    numberOfRuns: '',
    
    // Process Parameters (dynamic from backend)
    selectedProcessParameters: {}, // Object to store selected process parameters
    selectedQualityAttributes: {}, // Object to store selected quality attributes
    selectedBioreactorData: {}, // Object to store selected bioreactor data
    
    // Process Parameters
    process_temp: false,
    process_pressure: false,
    process_agitation: false,
    process_ph: false,
    process_do: false,
    process_co2: false,
    process_feedrate: false,
    process_feedcomp: false,
    process_custom: [], // Array of user-defined process parameters
    
    // Bioreactor Data
    reactor_str: false,
    reactor_air: false,
    reactor_packed: false,
    control_temp: false,
    control_ph: false,
    control_do: false,
    monitor_online: false,
    monitor_logging: false,
    bioreactor_custom: [], // Array of user-defined bioreactor parameters
    
    // Quality Attributes
    quality_titer: false,
    quality_purity: false,
    quality_bioactivity: false,
    quality_viability: false,
    quality_density: false,
    quality_growth: false,
    quality_consistency: false,
    quality_contamination: false,
    quality_custom: [], // Array of user-defined quality attributes
    
    // Success Criteria
    successCriteria: [
      {
        parameter: 'product_yield',
        operator: '>=',
        value: ''
      },
      {
        parameter: 'purity',
        operator: '>=',
        value: ''
      },
      {
        parameter: 'cell_viability',
        operator: '>=',
        value: ''
      },
      {
        parameter: 'process_duration',
        operator: '<=',
        value: ''
      }
    ]
  });
  
  // Default reference data - empty arrays to ensure API data is used
  const defaultReferenceData = {
    projectScales: [],
    projectTypes: [
      'Process Development',
      'Process Optimization',
      'Tech Transfer',
      'Scale Up',
      'Validation'
    ],
    successParameters: [
      'Product Yield',
      'Product Purity',
      'Cell Viability',
      'Process Duration',
      'Cost Efficiency',
      'Product Quality',
      'Contamination Level'
    ]
  };
  
  // Define load functions outside useEffect so they can be accessed by other functions
  const loadProcessParameters = async () => {
    try {
      console.log('ProjectModal: Loading process parameters...');
      const data = await fetchProcessParameters();
      console.log('ProjectModal: Process parameters loaded', {
        count: data.length,
        sample: data.slice(0, 2).map(p => ({
          id: p.id,
          name: p.name,
          processParameterType: p.processParameterType
        }))
      });
      setProcessParameters(data);
    } catch (error) {
      console.error('ProjectModal: Error loading process parameters:', error);
      setProcessParameters([]);
    }
  };

  const loadQualityAttributes = async () => {
    try {
      const data = await fetchQualityAttributes();
      setQualityAttributes(data);
    } catch (error) {
      console.error('Error loading quality attributes:', error);
      setQualityAttributes([]);
    }
  };

  const loadBioreactorData = async () => {
    try {
      const data = await fetchBioreactorData();
      setBioreactorData(data);
    } catch (error) {
      console.error('Error loading bioreactor data:', error);
      setBioreactorData([]);
    }
  };

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        setIsLoadingReferenceData(true);
        setReferenceDataError('');
        
        const API_BASE_URL = await getCachedApiUrl();
        const response = await fetch(`${API_BASE_URL}/api/reference-data`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch reference data: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        
        // Ensure we have the data we need
        if (!data.projectScales || !Array.isArray(data.projectScales) || data.projectScales.length === 0) {
          throw new Error('No project scales received from API');
        }
        
        setReferenceData({
          projectScales: data.projectScales,
          projectTypes: data.projectTypes || defaultReferenceData.projectTypes,
          successParameters: data.successParameters || defaultReferenceData.successParameters
        });
      } catch (error) {
        console.error('Error loading reference data:', error);
        setReferenceDataError(`Failed to load project scales: ${error.message}`);
        // Use empty array for projectScales to force user to fix the API issue
        setReferenceData({
          projectScales: [],
          projectTypes: defaultReferenceData.projectTypes,
          successParameters: defaultReferenceData.successParameters
        });
      } finally {
        setIsLoadingReferenceData(false);
      }
    };

    loadReferenceData();
    loadProcessParameters();
    loadQualityAttributes();
    loadBioreactorData();
    
    // Mark component as mounted
    isMountedRef.current = true;
    
    // Cleanup function to mark component as unmounted
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // New useEffect to populate form data when existingProject is provided
  useEffect(() => {
    if (readOnlyMode && existingProject) {
      setFormData({
        projectName: existingProject.projectName || '',
        projectOwner: existingProject.projectOwner || '',
        scale: existingProject.scale || '',
        type: existingProject.type || '',
        numberOfRuns: existingProject.numberOfRuns || '',
        
        // Dynamic properties from backend
        selectedProcessParameters: existingProject.selectedProcessParameters || {},
        selectedQualityAttributes: existingProject.selectedQualityAttributes || {},
        selectedBioreactorData: existingProject.selectedBioreactorData || {},
        
        // Process Parameters
        process_temp: existingProject.process_temp || false,
        process_pressure: existingProject.process_pressure || false,
        process_agitation: existingProject.process_agitation || false,
        process_ph: existingProject.process_ph || false,
        process_do: existingProject.process_do || false,
        process_co2: existingProject.process_co2 || false,
        process_feedrate: existingProject.process_feedrate || false,
        process_feedcomp: existingProject.process_feedcomp || false,
        process_custom: existingProject.process_custom || [],
        
        // Bioreactor Data
        reactor_str: existingProject.reactor_str || false,
        reactor_air: existingProject.reactor_air || false,
        reactor_packed: existingProject.reactor_packed || false,
        control_temp: existingProject.control_temp || false,
        control_ph: existingProject.control_ph || false,
        control_do: existingProject.control_do || false,
        monitor_online: existingProject.monitor_online || false,
        monitor_logging: existingProject.monitor_logging || false,
        bioreactor_custom: existingProject.bioreactor_custom || [],
        
        // Quality Attributes
        quality_titer: existingProject.quality_titer || false,
        quality_purity: existingProject.quality_purity || false,
        quality_bioactivity: existingProject.quality_bioactivity || false,
        quality_viability: existingProject.quality_viability || false,
        quality_density: existingProject.quality_density || false,
        quality_growth: existingProject.quality_growth || false,
        quality_consistency: existingProject.quality_consistency || false,
        quality_contamination: existingProject.quality_contamination || false,
        quality_custom: existingProject.quality_custom || [],
        
        // Success Criteria
        successCriteria: existingProject.successCriteria || [
          { parameter: 'product_yield', operator: '>=', value: '' },
          { parameter: 'purity', operator: '>=', value: '' },
          { parameter: 'cell_viability', operator: '>=', value: '' },
          { parameter: 'process_duration', operator: '<=', value: '' }
        ]
      });    }
  }, [readOnlyMode, existingProject]);  // Handle form reset when modal is closed
  useEffect(() => {
    if (!show && !readOnlyMode) {
      // Reset form when modal is closed (but not in read-only mode)
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current && !isSubmitting) {
          resetForm();
          setIsClosing(false); // Reset closing state
          setValidationErrors({});
          setSubmitError('');
          setSubmitSuccess('');
        }
      }, 300); // Small delay to ensure modal transition is complete
      
      return () => clearTimeout(timeoutId);
    }
  }, [show, readOnlyMode, isSubmitting]);

  // Safe modal close function to prevent DOM manipulation errors
  const handleSafeClose = () => {
    if (isClosing || !isMountedRef.current || isSubmitting) return;
    
    setIsClosing(true);
    onHide();
  };
  const handleInputChange = (e) => {
    if (readOnlyMode) return; // Prevent changes in read-only mode
    
    const { name, checked, type } = e.target;
    const value = type === 'checkbox' ? checked : e.target.value;
    
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  const handleAddCustomAttribute = (section) => {
    if (readOnlyMode) return; // Prevent changes in read-only mode
      const customAttrName = prompt(`Enter new ${section} attribute name:`);
    if (customAttrName) {
      const newAttr = {
        id: `${section}_${Date.now()}`,
        name: customAttrName,
        value: false
      };

      setFormData(prevData => ({
        ...prevData,
        [`${section}_custom`]: [...prevData[`${section}_custom`], newAttr]
      }));
    }
  };

  const handleEditCustomAttribute = (section, attrId) => {
    const customAttrs = formData[`${section}_custom`];
    const attr = customAttrs.find(a => a.id === attrId);
    if (!attr) return;

    const newName = prompt('Enter new name:', attr.name);
    if (newName) {
      setFormData(prevData => ({
        ...prevData,
        [`${section}_custom`]: prevData[`${section}_custom`].map(a => 
          a.id === attrId ? { ...a, name: newName } : a
        )
      }));
    }
  };

  const handleDeleteCustomAttribute = (section, attrId) => {
    if (window.confirm('Are you sure you want to delete this attribute?')) {
      setFormData(prevData => ({
        ...prevData,
        [`${section}_custom`]: prevData[`${section}_custom`].filter(a => a.id !== attrId)
      }));
    }
  };

  const handleCustomAttributeChange = (section, attrId) => {
    setFormData(prevData => ({
      ...prevData,
      [`${section}_custom`]: prevData[`${section}_custom`].map(a => 
        a.id === attrId ? { ...a, value: !a.value } : a
      )
    }));
  };

  // Handle process parameter checkbox changes
  const handleProcessParameterChange = (parameterName, checked) => {
    setFormData(prev => ({
      ...prev,
      selectedProcessParameters: {
        ...prev.selectedProcessParameters,
        [parameterName]: checked
      }
    }));
  };
  // Handle quality attribute checkbox changes
  const handleQualityAttributeChange = (attributeName, checked) => {
    setFormData(prev => ({
      ...prev,
      selectedQualityAttributes: {
        ...prev.selectedQualityAttributes,
        [attributeName]: checked
      }
    }));
  };

  // Handle bioreactor data checkbox changes
  const handleBioreactorDataChange = (dataName, checked) => {
    setFormData(prev => ({
      ...prev,
      selectedBioreactorData: {
        ...prev.selectedBioreactorData,
        [dataName]: checked
      }
    }));
  };

  // Handle process parameter type updates (for drag and drop)
  const handleParameterTypeUpdate = async (parameterId, newType) => {
    try {
      console.log('ProjectModal: handleParameterTypeUpdate called', { 
        parameterId, 
        newType,
        currentProcessParameters: processParameters.length 
      });
      
      // Call API to update parameter type
      await updateProcessParameterType(parameterId, newType);
      console.log('ProjectModal: API call successful, reloading process parameters');
      
      // Reload process parameters to reflect the change from database
      await loadProcessParameters();
      console.log('ProjectModal: Process parameters reloaded successfully');
      
      // Force re-render by updating a timestamp or counter
      setValidationErrors(prev => ({ ...prev, _lastUpdate: Date.now() }));
      
    } catch (error) {
      console.error('ProjectModal: Error updating parameter type:', error);
      
      // Show user-friendly error message
      const errorMessage = error.message || 'Failed to update parameter type';
      alert(`Error updating parameter type: ${errorMessage}`);
      
      // Reload parameters to ensure UI is in sync with database
      try {
        await loadProcessParameters();
      } catch (reloadError) {
        console.error('ProjectModal: Error reloading parameters after failed update:', reloadError);
      }
      
      // Re-throw error so the component can handle it
      throw error;
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Project Details validation
    if (!formData.projectName) {
      errors.projectName = 'Project name is required';
    }
    if (!formData.projectOwner) {
      errors.projectOwner = 'Project owner is required';
    }
    if (!formData.scale) {
      errors.scale = 'Scale is required';
    }
    if (!formData.type) {
      errors.type = 'Type is required';
    }
    if (!formData.numberOfRuns || formData.numberOfRuns < 1) {
      errors.numberOfRuns = 'Number of runs must be at least 1';
    }    // Process Parameters validation
    const selectedProcessParams = Object.keys(formData.selectedProcessParameters || {}).filter(
      key => formData.selectedProcessParameters && formData.selectedProcessParameters[key]
    );
    if (selectedProcessParams.length === 0) {
      errors.processParameters = 'Select at least one process parameter';
    }

    // Quality Attributes validation
    const selectedQualityAttrs = Object.keys(formData.selectedQualityAttributes || {}).filter(
      key => formData.selectedQualityAttributes && formData.selectedQualityAttributes[key]
    );
    if (selectedQualityAttrs.length === 0) {
      errors.qualityAttributes = 'Select at least one quality attribute';
    }

    // Bioreactor Data validation
    const selectedBioreactorParams = Object.keys(formData.selectedBioreactorData || {}).filter(
      key => formData.selectedBioreactorData && formData.selectedBioreactorData[key]
    );
    if (selectedBioreactorParams.length === 0) {
      errors.bioreactorData = 'Select at least one bioreactor parameter';
    }

    // Success Criteria validation
    if (!formData.successCriteria || formData.successCriteria.length === 0) {
      errors.successCriteria = 'At least one success criterion is required';
    } else {
      const invalidCriteria = formData.successCriteria.some(
        criterion => !criterion.parameter || !criterion.value || 
        (criterion.operator === 'range' && (!criterion.minValue || !criterion.maxValue))
      );
      if (invalidCriteria) {
        errors.successCriteria = 'All success criteria fields must be filled';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitError('');
      setSubmitSuccess('');
        try {
        // Convert numberOfRuns to integer before sending to API
        const projectData = {
          ...formData,
          numberOfRuns: parseInt(formData.numberOfRuns, 10)
        };
        
        // Debug: Log the data being sent to see if custom parameters are included
        console.log('Submitting project data:', projectData);
        console.log('Process custom parameters:', projectData.process_custom);
        console.log('Quality custom parameters:', projectData.quality_custom);
        console.log('Bioreactor custom parameters:', projectData.bioreactor_custom);
        
        const response = await createProject(projectData);
        
        // Store projectId in session storage for use across the application
        sessionStorage.setItem('currentProjectId', response.projectId);
        
        // Also store project in localStorage for ProjectLanding component access
        const storedProjects = JSON.parse(localStorage.getItem('projects') || '[]');
        const projectWithTimestamp = {
          ...response,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        storedProjects.push(projectWithTimestamp);
        localStorage.setItem('projects', JSON.stringify(storedProjects));        setSubmitSuccess('Project created successfully!');
        
        // Reset form immediately to prevent further submissions
        resetForm();
        
        // Call parent callback - parent will handle modal closing
        if (onProjectCreated) {
          onProjectCreated(response);
        }        
      } catch (error) {
        setSubmitError(error.message || 'Failed to create project');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Find the first tab with an error and switch to it
      const tabsWithErrors = {
        details: ['projectName', 'projectOwner', 'scale', 'type', 'numberOfRuns'],
        'process-quality': ['processParameters', 'qualityAttributes'],
        bioreactor: ['bioreactorData'],
        success: ['successCriteria']
      };

      for (const [tab, fields] of Object.entries(tabsWithErrors)) {
        if (fields.some(field => validationErrors[field])) {
          setActiveTab(tab);
          break;
        }
      }
    }
  };
  const resetForm = () => {
    setFormData({
      // Project Details
      projectName: '',
      projectOwner: '',
      scale: '',
      type: '',
      numberOfRuns: '',
      
      // Process Parameters (dynamic from backend)
      selectedProcessParameters: {}, // Object to store selected process parameters
      selectedQualityAttributes: {}, // Object to store selected quality attributes
      selectedBioreactorData: {}, // Object to store selected bioreactor data
      
      // Process Parameters
      process_temp: false,
      process_pressure: false,
      process_agitation: false,
      process_ph: false,
      process_do: false,
      process_co2: false,
      process_feedrate: false,
      process_feedcomp: false,
      process_custom: [],
      
      // Bioreactor Data
      reactor_str: false,
      reactor_air: false,
      reactor_packed: false,
      control_temp: false,
      control_ph: false,
      control_do: false,
      monitor_online: false,
      monitor_logging: false,
      bioreactor_custom: [],
      
      // Quality Attributes
      quality_titer: false,
      quality_purity: false,
      quality_bioactivity: false,
      quality_viability: false,
      quality_density: false,
      quality_growth: false,
      quality_consistency: false,
      quality_contamination: false,
      quality_custom: [],
      
      // Success Criteria
      successCriteria: [
        {
          parameter: 'product_yield',
          operator: '>=',
          value: ''
        },
        {
          parameter: 'purity',
          operator: '>=',
          value: ''
        },
        {
          parameter: 'cell_viability',
          operator: '>=',
          value: ''
        },
        {
          parameter: 'process_duration',
          operator: '<=',
          value: ''
        }
      ]
    });
    setValidationErrors({});
    setSubmitError('');
    setSubmitSuccess('');
    setActiveTab('details');
  };

  // Helper function to check if a tab has validation errors
  const hasTabErrors = (tabKey) => {
    const tabErrorFields = {
      details: ['projectName', 'projectOwner', 'scale', 'type', 'numberOfRuns'],
      'process-quality': ['processParameters', 'qualityAttributes'],
      bioreactor: ['bioreactorData'],
      success: ['successCriteria']
    };
    
    const fields = tabErrorFields[tabKey] || [];
    return fields.some(field => validationErrors[field]);
  };

  // Helper function to create tab title with error indicator
  const getTabTitle = (title, tabKey) => (
    <span className={hasTabErrors(tabKey) ? 'tab-with-error' : ''}>
      {title}
      {hasTabErrors(tabKey) && <span className="error-indicator"> ⚠️</span>}
    </span>
  );

  return (    <Modal 
      show={show} 
      onHide={isClosing || isSubmitting ? undefined : handleSafeClose} 
      size="lg" 
      className="project-modal"
      backdrop={isClosing || isSubmitting ? "static" : true}
      keyboard={!isClosing && !isSubmitting}
    >      <Modal.Header closeButton={!isClosing && !isSubmitting}>
        <Modal.Title>{readOnlyMode ? 'Project Settings' : 'Create New Project'}</Modal.Title>
      </Modal.Header><Modal.Body>
        {submitError && (
          <Alert variant="danger" className="mb-3">
            {submitError}
          </Alert>
        )}
        {submitSuccess && (
          <Alert variant="success" className="mb-3">
            {submitSuccess}
          </Alert>
        )}
        <Form onSubmit={readOnlyMode ? (e) => e.preventDefault() : handleSubmit}>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-4"
          >
            <Tab eventKey="details" title={getTabTitle("Project Details", "details")}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Project Name</Form.Label>                    <Form.Control
                      type="text"
                      name="projectName"
                      value={formData.projectName}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.projectName}
                      disabled={readOnlyMode}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.projectName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Project Owner</Form.Label>                    <Form.Control
                      type="text"
                      name="projectOwner"
                      value={formData.projectOwner}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.projectOwner}
                      disabled={readOnlyMode}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.projectOwner}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Scale</Form.Label>                    <Form.Select
                      name="scale"
                      value={formData.scale}
                      onChange={handleInputChange}
                      required
                      isInvalid={!!validationErrors.scale}
                      disabled={readOnlyMode || isLoadingReferenceData}
                    >
                      {isLoadingReferenceData ? (
                        <option value="">Loading scales...</option>
                      ) : referenceDataError ? (
                        <option value="">Error loading scales</option>
                      ) : referenceData.projectScales.length === 0 ? (
                        <option value="">No scales available</option>
                      ) : (
                        <>
                          <option value="">Select Scale</option>
                          {referenceData.projectScales.map(scale => (
                            <option key={scale} value={scale}>{scale}</option>
                          ))}
                        </>
                      )}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.scale}
                    </Form.Control.Feedback>
                    {referenceDataError && (
                      <Form.Text className="text-danger">
                        {referenceDataError}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Type</Form.Label>                    <Form.Select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      required
                      isInvalid={!!validationErrors.type}
                      disabled={readOnlyMode}
                    >
                      <option value="">Select Type</option>
                      {referenceData.projectTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.type}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Number of Runs</Form.Label>                    <Form.Control
                      type="number"
                      name="numberOfRuns"
                      value={formData.numberOfRuns}
                      onChange={handleInputChange}
                      required
                      min="1"
                      placeholder="Enter number of runs"
                      isInvalid={!!validationErrors.numberOfRuns}
                      disabled={readOnlyMode}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.numberOfRuns}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="process-quality" title={getTabTitle("Process & Quality", "process-quality")}>
              <div className="process-quality-container">
                <div className="process-section">
                  <ProcessParametersSection
                    processParameters={processParameters}
                    selectedProcessParameters={formData.selectedProcessParameters}
                    onParameterChange={handleProcessParameterChange}
                    onParameterTypeUpdate={handleParameterTypeUpdate}
                    readOnlyMode={readOnlyMode}
                    validationErrors={validationErrors}
                  />

                  <div className="custom-attributes-section">
                    <h6>
                      Custom Process Parameters
                      <span className="custom-attributes-count">
                        {formData.process_custom.length} custom
                      </span>
                    </h6>
                    {formData.process_custom.map(attr => (
                      <div key={attr.id} className="custom-attribute-item">
                        <Form.Check
                          type="checkbox"
                          label={attr.name}
                          checked={attr.value}
                          onChange={() => handleCustomAttributeChange('process', attr.id)}
                          disabled={readOnlyMode}
                        />
                        <div className="custom-attribute-controls">
                          <button
                            type="button"
                            className="custom-attribute-btn"
                            onClick={() => handleEditCustomAttribute('process', attr.id)}
                            title="Edit parameter name"
                            disabled={readOnlyMode}
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="custom-attribute-btn delete"
                            onClick={() => handleDeleteCustomAttribute('process', attr.id)}
                            title="Delete parameter"
                            disabled={readOnlyMode}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                    {!readOnlyMode && (
                      <div
                        className="add-custom-attribute"
                        onClick={() => handleAddCustomAttribute('process')}
                        title="Add a new custom process parameter"
                      >
                        <i>+</i> Add Custom Parameter
                      </div>
                    )}
                  </div>
                </div>

                <div className="quality-section">
                  <h5>Quality Attributes</h5>
                  {validationErrors.qualityAttributes && (
                    <div className="validation-error mb-3">
                      {validationErrors.qualityAttributes}
                    </div>
                  )}
                  <div className="checkbox-list-container">
                    <div className="checkbox-group">
                      {qualityAttributes.map(attr => {
                        // Get badge info based on qualityAttributeType
                        const getBadgeInfo = (qualityAttributeType) => {
                          if (qualityAttributeType === 'output') {
                            return { text: 'Output', className: 'tag-output' };
                          }
                          // Default fallback (in case of future types)
                          return { text: 'Output', className: 'tag-output' };
                        };
                        
                        const badgeInfo = getBadgeInfo(attr.qualityAttributeType);
                        
                        return (
                          <div key={attr.name} className="parameter-item-with-tag">
                            <Form.Check
                              type="checkbox"
                              label={attr.name}
                              name={attr.name}
                              id={attr.name.replace(/\s+/g, '_').toLowerCase()}
                              checked={formData.selectedQualityAttributes[attr.name] || false}
                              onChange={(e) => handleQualityAttributeChange(attr.name, e.target.checked)}
                              disabled={readOnlyMode}
                              title={attr.description || attr.name}
                              className="parameter-checkbox-with-tag"
                            />
                            <span className={`parameter-tag ${badgeInfo.className}`}>
                              {badgeInfo.text}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {/* Custom Quality Attributes */}
                    {formData.quality_custom.length > 0 && (
                      <div className="custom-parameters-inline">
                        {formData.quality_custom.map(attr => (
                          <div key={attr.id} className="parameter-item-with-tag">
                            <Form.Check
                              type="checkbox"
                              label={attr.name}
                              checked={attr.value}
                              onChange={() => handleCustomAttributeChange('quality', attr.id)}
                              disabled={readOnlyMode}
                              title={`Custom quality attribute: ${attr.name}`}
                              className="custom-parameter-checkbox parameter-checkbox-with-tag"
                            />
                            <span className="parameter-tag tag-output">Output</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="custom-attributes-section">
                    <h6>
                      Custom Quality Attributes
                      <span className="custom-attributes-count">
                        {formData.quality_custom.length} custom
                      </span>
                    </h6>
                    {formData.quality_custom.map(attr => (
                      <div key={attr.id} className="custom-attribute-item">
                        <Form.Check
                          type="checkbox"
                          label={attr.name}
                          checked={attr.value}
                          onChange={() => handleCustomAttributeChange('quality', attr.id)}
                          disabled={readOnlyMode}
                        />
                        <div className="custom-attribute-controls">
                          <button
                            type="button"
                            className="custom-attribute-btn"
                            onClick={() => handleEditCustomAttribute('quality', attr.id)}
                            title="Edit attribute name"
                            disabled={readOnlyMode}
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="custom-attribute-btn delete"
                            onClick={() => handleDeleteCustomAttribute('quality', attr.id)}
                            title="Delete attribute"
                            disabled={readOnlyMode}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                    {!readOnlyMode && (
                      <div
                        className="add-custom-attribute"
                        onClick={() => handleAddCustomAttribute('quality')}
                        title="Add a new custom quality attribute"
                      >
                        <i>+</i> Add Custom Attribute
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Tab>            <Tab eventKey="bioreactor" title={getTabTitle("Bioreactor Data", "bioreactor")}>
              <div className="bioreactor-section">
                <h5>Bioreactor Configuration</h5>
                {validationErrors.bioreactorData && (
                  <div className="validation-error mb-3">
                    {validationErrors.bioreactorData}
                  </div>
                )}
                <div className="checkbox-list-container">
                  <div className="checkbox-group-two-column">
                    {bioreactorData.map(parameter => {
                      // Get badge info based on BioreactorParameterType
                      const getBadgeInfo = (bioreactorParameterType) => {
                        if (bioreactorParameterType === 'Input Bioreactor Parameter') {
                          return { text: 'Input', className: 'tag-input' };
                        } else if (bioreactorParameterType === 'Output Bioreactor Parameter') {
                          return { text: 'Output', className: 'tag-output' };
                        }
                        // Default fallback for parameters without type
                        return { text: 'Input', className: 'tag-input' };
                      };
                      
                      const badgeInfo = getBadgeInfo(parameter.BioreactorParameterType);
                      
                      return (
                        <div key={parameter.name} className="parameter-item-with-tag">
                          <Form.Check
                            type="checkbox"
                            label={parameter.name}
                            name={parameter.name}
                            id={parameter.name.replace(/\s+/g, '_').toLowerCase()}
                            checked={formData.selectedBioreactorData[parameter.name] || false}
                            onChange={(e) => handleBioreactorDataChange(parameter.name, e.target.checked)}
                            disabled={readOnlyMode}
                            title={parameter.description || parameter.name}
                            className="parameter-checkbox-with-tag"
                          />
                          <span className={`parameter-tag ${badgeInfo.className}`}>
                            {badgeInfo.text}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Custom Bioreactor Parameters */}
                  {formData.bioreactor_custom.length > 0 && (
                    <div className="custom-parameters-inline">
                      <h6 className="custom-parameters-header">Custom Configuration</h6>
                      {formData.bioreactor_custom.map(attr => (
                        <div key={attr.id} className="parameter-item-with-tag">
                          <Form.Check
                            type="checkbox"
                            label={attr.name}
                            checked={attr.value}
                            onChange={() => handleCustomAttributeChange('bioreactor', attr.id)}
                            disabled={readOnlyMode}
                            title={`Custom bioreactor parameter: ${attr.name}`}
                            className="custom-parameter-checkbox parameter-checkbox-with-tag"
                          />
                          <span className="parameter-tag tag-input">Input</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {!readOnlyMode && (
                  <div
                    className="add-custom-attribute"
                    onClick={() => handleAddCustomAttribute('bioreactor')}
                    title="Add a new custom bioreactor configuration"
                  >
                    <i>+</i> Add Custom Configuration
                  </div>
                )}
              </div>
            </Tab>

            <Tab eventKey="success" title={getTabTitle("Success Criteria", "success")}>
              <div className="success-criteria-container">
                {validationErrors.successCriteria && (
                  <div className="validation-error mb-3">{validationErrors.successCriteria}</div>
                )}
                <div className="criteria-header d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Define Success Criteria</h6>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    disabled={readOnlyMode}
                    onClick={() => {
                      const newCriteria = {
                        parameter: '',
                        operator: '>=',
                        value: ''
                      };
                      setFormData(prev => ({
                        ...prev,
                        successCriteria: [...(prev.successCriteria || []), newCriteria]
                      }));
                    }}
                  >
                    + Add Criterion
                  </Button>
                </div>

                {(formData.successCriteria || []).map((criterion, index) => (
                  <div key={index} className="criteria-row">
                    <Row className="align-items-center mb-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Parameter</Form.Label>
                          <Form.Select
                            value={criterion.parameter}
                            onChange={(e) => {
                              const newCriteria = [...(formData.successCriteria || [])];
                              newCriteria[index].parameter = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                successCriteria: newCriteria
                              }));
                            }}
                          >
                            <option value="">Select Parameter</option>
                            {referenceData.successParameters.map(param => (
                              <option key={param} value={param}>{param}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Operator</Form.Label>
                          <Form.Select
                            value={criterion.operator}
                            onChange={(e) => {
                              const newCriteria = [...(formData.successCriteria || [])];
                              newCriteria[index].operator = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                successCriteria: newCriteria
                              }));
                            }}
                          >
                            <option value=">=">≥ (Greater than or equal)</option>
                            <option value="<=">≤ (Less than or equal)</option>
                            <option value="=">=  (Equal to)</option>
                            <option value=">">＞ (Greater than)</option>
                            <option value="<">＜ (Less than)</option>
                            <option value="range">Range</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Value</Form.Label>
                          {criterion.operator === 'range' ? (
                            <Row>
                              <Col>
                                <Form.Control
                                  type="number"
                                  placeholder="Min"
                                  value={criterion.minValue || ''}
                                  onChange={(e) => {
                                    const newCriteria = [...(formData.successCriteria || [])];
                                    newCriteria[index].minValue = e.target.value;
                                    setFormData(prev => ({
                                      ...prev,
                                      successCriteria: newCriteria
                                    }));
                                  }}
                                />
                              </Col>
                              <Col>
                                <Form.Control
                                  type="number"
                                  placeholder="Max"
                                  value={criterion.maxValue || ''}
                                  onChange={(e) => {
                                    const newCriteria = [...(formData.successCriteria || [])];
                                    newCriteria[index].maxValue = e.target.value;
                                    setFormData(prev => ({
                                      ...prev,
                                      successCriteria: newCriteria
                                    }));
                                  }}
                                />
                              </Col>
                            </Row>
                          ) : (
                            <Form.Control
                              type="number"
                              placeholder="Enter value"
                              value={criterion.value || ''}
                              onChange={(e) => {
                                const newCriteria = [...(formData.successCriteria || [])];
                                newCriteria[index].value = e.target.value;
                                setFormData(prev => ({
                                  ...prev,
                                  successCriteria: newCriteria
                                }));
                              }}
                            />
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={1}>
                        <Button
                          variant="link"
                          className="text-danger delete-criterion"
                          onClick={() => {
                            const newCriteria = [...(formData.successCriteria || [])];
                            newCriteria.splice(index, 1);
                            setFormData(prev => ({
                              ...prev,
                              successCriteria: newCriteria
                            }));
                          }}
                        >
                          ✕
                        </Button>
                      </Col>
                    </Row>
                  </div>
                ))}
                
                {(!formData.successCriteria || formData.successCriteria.length === 0) && (
                  <div className="text-center text-muted py-4">
                    <p>No success criteria defined. Click "Add Criterion" to begin.</p>
                  </div>
                )}
              </div>
            </Tab>
          </Tabs>          <div className="d-flex justify-content-end mt-4">
            <Button variant="secondary" onClick={onHide} className="me-2" disabled={isSubmitting}>
              {readOnlyMode ? 'Close' : 'Cancel'}
            </Button>
            {!readOnlyMode && (
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  'Create Project'
                )}
              </Button>
            )}
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default ProjectModal;
